// Lib
import deleteTextFromStringByIndex from '@/utils/string/deleteTextFromStringByIndex';
// Utils
import {
    checkCaptionValidation,
    validateHashtags,
    validateLength,
    validateMentions
} from '@/utils/hooks/utils/useValidateCaption';

import { hashtagRegex, mentionRegex } from '@/lib/regex';
import {
    INSTAGRAM,
    LINKEDIN,
    THREADS,
    TIKTOK_BUSINESS,
    TWITTER,
    X,
    YOUTUBE
} from '@/lib/socialProfile';

export const instagramCaptionRestrictions = [
    { type: 'characters', restriction: { limit: 2200, warning: 50 }, validate: validateLength },
    { type: 'hashtags', restriction: { limit: 30, warning: 1 }, validate: validateHashtags },
    { type: 'mentions', restriction: { limit: 20, warning: 1 }, validate: validateMentions }
];

export const instagramFirstCommentRestrictions = [
    { type: 'hashtags', restriction: { limit: 30, warning: 1 }, validate: validateHashtags }
];

export const twitterCaptionRestrictions = [
    { type: 'characters', restriction: { limit: 280, warning: 20 }, validate: validateLength }
];

export const xCaptionRestrictions = [
    { type: 'characters', restriction: { limit: 280, warning: 20 }, validate: validateLength }
];

export const linkedinCaptionRestrictions = [
    { type: 'characters', restriction: { limit: 3000, warning: 20 }, validate: validateLength }
];

export const tiktokBusinessCaptionRestrictions = [
    { type: 'characters', restriction: { limit: 2200, warning: 50 }, validate: validateLength }
];

export const tiktokBusinessCaptionImagesRestrictions = [
    { type: 'characters', restriction: { limit: 4000, warning: 50 }, validate: validateLength }
];

export const threadsCaptionRestrictions = [
    { type: 'characters', restriction: { limit: 500, warning: 20 }, validate: validateLength }
];

export const youtubeCaptionRestrictions = [
    { type: 'characters', restriction: { limit: 5000, warning: 50 }, validate: validateLength }
];

export function getCaptionRestriction(type, postType) {
    switch (type) {
        case INSTAGRAM:
            return instagramCaptionRestrictions;
        case TWITTER:
        case X:
            return twitterCaptionRestrictions;
        case LINKEDIN:
            return linkedinCaptionRestrictions;
        case TIKTOK_BUSINESS:
            if (postType === 'image' || postType === 'carousel') {
                return tiktokBusinessCaptionImagesRestrictions;
            }
            return tiktokBusinessCaptionRestrictions;
        case THREADS:
            return threadsCaptionRestrictions;
        case YOUTUBE:
            return youtubeCaptionRestrictions;
        default:
            return null;
    }
}

export const getAICaptionsRestrictions = (type) => {
    switch (type) {
        case TWITTER:
        case X:
            return twitterCaptionRestrictions.find((item) => item.type === 'characters').restriction
                .limit;
        case YOUTUBE:
            return youtubeCaptionRestrictions.find((item) => item.type === 'characters').restriction
                .limit;
        default:
            return 800;
    }
};

export function getFirstCommentRestriction(type) {
    switch (type) {
        case INSTAGRAM:
        default:
            return instagramFirstCommentRestrictions;
    }
}

const listIndexes = (newCaption) => {
    let match;
    let i = 0;
    let listIndexHashtags = [];
    try {
        while ((match = hashtagRegex.exec(newCaption))) {
            if (i > 200) {
                throw new Error('error');
            }
            listIndexHashtags.push({
                start: match.index,
                end: hashtagRegex.lastIndex
            });
            i++;
        }
    } catch (e) {
        listIndexHashtags = null;
    }
    return listIndexHashtags;
};

const listIndexesMentions = (newCaption) => {
    let match;
    let i = 0;
    let listIndexMentions = [];
    try {
        while ((match = mentionRegex.exec(newCaption))) {
            if (i > 200) {
                throw new Error('error');
            }
            listIndexMentions.push({
                start: match.index,
                end: mentionRegex.lastIndex
            });
            i++;
        }
    } catch (e) {
        listIndexMentions = null;
    }
    return listIndexMentions;
};

const removeHashtagExceed = ({ caption = '', maxHashtags = 30 }) => {
    let newCaption = caption;
    let listIndexHashtags = listIndexes(newCaption);

    try {
        let i = 0;
        if (listIndexHashtags) {
            while (listIndexHashtags?.length > maxHashtags) {
                if (i > 200) {
                    throw new Error('error');
                }
                newCaption = deleteTextFromStringByIndex(
                    newCaption,
                    listIndexHashtags?.[maxHashtags]?.start,
                    listIndexHashtags?.[maxHashtags]?.end
                );
                listIndexHashtags = listIndexes(newCaption);
                i++;
            }
        } else {
            newCaption = '';
        }
    } catch (e) {
        newCaption = '';
    }

    return newCaption;
};

const removeMentionExceed = ({ caption = '', maxMentions = 20 }) => {
    let newCaption = caption;
    let listIndexMentions = listIndexesMentions(newCaption);

    try {
        let i = 0;
        if (listIndexMentions) {
            while (listIndexMentions?.length > maxMentions) {
                if (i > 200) {
                    throw new Error('error');
                }
                newCaption = deleteTextFromStringByIndex(
                    newCaption,
                    listIndexMentions?.[maxMentions]?.start,
                    listIndexMentions?.[maxMentions]?.end
                );
                listIndexMentions = listIndexesMentions(newCaption);
                i++;
            }
        } else {
            newCaption = '';
        }
    } catch (e) {
        newCaption = '';
    }

    return newCaption;
};

// behaviour specific to crosspost
export const formatCaption = (caption, captionRestrictions) => {
    const dataFirstCheck = checkCaptionValidation(caption, captionRestrictions);
    let captionFormatted = caption;
    let error = null;
    // Delete caption
    if (dataFirstCheck && 'isHashtagsValid' in dataFirstCheck && !dataFirstCheck.isHashtagsValid) {
        const maxHashtags = captionRestrictions.find(({ type }) => type === 'hashtags').restriction
            .limit;
        captionFormatted = removeHashtagExceed({ caption: captionFormatted, maxHashtags });
        error = 'scheduler:scheduler_funnel.cross_post.error_cross_post_caption_hashtag_exceed';
    }

    const dataSecondCheck = checkCaptionValidation(captionFormatted, captionRestrictions);
    if (
        dataSecondCheck &&
        'isMentionsValid' in dataSecondCheck &&
        !dataSecondCheck.isMentionsValid
    ) {
        const maxMentions = captionRestrictions.find(({ type }) => type === 'mentions').restriction
            .limit;

        captionFormatted = removeMentionExceed({ caption: captionFormatted, maxMentions });
        error = 'scheduler:scheduler_funnel.cross_post.error_cross_post_caption_mention_exceed';
    }

    const dataThirdCheck = checkCaptionValidation(captionFormatted, captionRestrictions);
    if (dataThirdCheck && 'isLengthValid' in dataThirdCheck && !dataThirdCheck.isLengthValid) {
        const limit = captionRestrictions.find(({ type }) => type === 'characters').restriction
            .limit;

        captionFormatted = captionFormatted.substring(0, limit);
        error = 'scheduler:scheduler_funnel.cross_post.error_cross_post_caption_cropped';
    }

    return { captionFormatted, error };
};
